import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useMaterialSearchStore } from '$/components/core/Collection/MaterialSearch/store/useMaterialSearchStore';
import { type SpectrumWidgetRequest } from '$/services/usecases/materials';
import { type MaterialType } from '$/services/usecases/materials/mapper/materials';
import { infiniteMaterialsQuery } from '$/services/usecases/materials/queries';
import { useCollectionStore } from '$/stores/useCollectionStore';

export type UseInfiniteMaterialsPayload = {
  type?: MaterialType;
  limit?: number;
  hideProMaterials?: boolean;
  orderByFanPage?: boolean;
  enabled?: boolean;
  spectrumWidget?: SpectrumWidgetRequest;
  includeCredentials?: boolean;
};

export const useInfiniteMaterials = (opt: UseInfiniteMaterialsPayload = {}) => {
  const brightnessFilter = useCollectionStore.useBrightnessFilter();
  const filterOptions = useCollectionStore.useAppliedFilterOptions();
  const query = useMaterialSearchStore.useQuery();

  const [showSkeleton, setShowSkeleton] = useState(false);

  const hasCollectionFilter =
    opt.type === 'color_tint' &&
    filterOptions.some((option) => option.option === 'collection');

  const queryRes = useInfiniteQuery(
    infiniteMaterialsQuery(
      {
        limit: opt.limit ?? 100,
        filter: filterOptions,
        brightnessFilter,
        type: opt.type ?? 'color_tint',
        query: query,
        spectrumWidget: opt.spectrumWidget,
        includeCredentials: opt.includeCredentials,
        order:
          hasCollectionFilter || opt.orderByFanPage
            ? 'page asc, pagePosition asc'
            : undefined,
      },
      opt.enabled === false,
    ),
  );

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (queryRes.isLoading) {
      timer = setTimeout(() => setShowSkeleton(true), 500);
    } else {
      setShowSkeleton(false);
    }
    return () => clearTimeout(timer);
  }, [queryRes.isLoading]);

  const materials = useMemo(
    () => queryRes.data?.pages.flatMap((page) => page.results),
    [queryRes.data],
  );
  const firstPage = queryRes.data?.pages[0];
  const count = firstPage?.colorCount ?? firstPage?.count ?? 0;

  return [queryRes, showSkeleton, materials, count] as const;
};
